import { useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useLayout } from '@/contexts/LayoutContext';
import { Layout, Check, Sparkles, ZoomIn, X } from 'lucide-react';
import { secureSetItem, secureGetItem } from '@/lib/storage';
import layout1Preview from '@/assets/layout-1.png';
import layout2Preview from '@/assets/layout-2.png';
import layout3Preview from '@/assets/layout-3.png';
import layout4Preview from '@/assets/layout-4.png';
import layout5Preview from '@/assets/layout-5.png';
import layout6Preview from '@/assets/layout-6.png';
import layout7Preview from '@/assets/layout-7.png';
import type { LayoutType } from '@/contexts/LayoutContext';
import { cn } from '@/lib/utils';

const MANUAL_SELECTION_FLAG_KEY = 'layout-manual-selection';

type SelectableLayout = 'auto' | LayoutType;

const LAYOUT_LABELS: Record<LayoutType, string> = {
  'layout-1': 'Layout 1',
  'layout-2': 'Layout 2',
  'layout-3': 'Layout 3',
  'layout-4': 'Layout 4',
  'layout-5': 'Layout 5',
  'layout-6': 'Layout 6',
  'layout-7': 'Layout 7',
};

const LAYOUT_PREVIEW_IMAGES: Record<LayoutType, string> = {
  'layout-1': layout1Preview,
  'layout-2': layout2Preview,
  'layout-3': layout3Preview,
  'layout-4': layout4Preview,
  'layout-5': layout5Preview,
  'layout-6': layout6Preview,
  'layout-7': layout7Preview,
};

type LayoutOption = {
  id: SelectableLayout;
  name: string;
  description: string;
  tag: string;
  image: string | null;
  isAuto: boolean;
};

interface LayoutSelectionModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const LayoutSelectionModal = ({ isOpen, onOpenChange }: LayoutSelectionModalProps) => {
  const { currentLayout, defaultLayout } = useLayout();
  const [selectedLayout, setSelectedLayout] = useState<SelectableLayout>(currentLayout);
  const [isManualSelection, setIsManualSelection] = useState(false);
  const [isChangingLayout, setIsChangingLayout] = useState(false);
  const [pendingLayout, setPendingLayout] = useState<SelectableLayout | null>(null);
  const [expandedPreview, setExpandedPreview] = useState<LayoutOption | null>(null);

  useEffect(() => {
    const checkManualSelection = async () => {
      const manualSelection = await secureGetItem(MANUAL_SELECTION_FLAG_KEY);
      const isManual = manualSelection === true ||
        manualSelection === 'true' ||
        (typeof manualSelection === 'object' && manualSelection?.value === true);
      setIsManualSelection(isManual);
      if (!isManual) {
        setSelectedLayout('auto');
      } else {
        setSelectedLayout(currentLayout);
      }
    };
    if (isOpen) {
      checkManualSelection();
      setExpandedPreview(null);
    }
  }, [isOpen, currentLayout]);

  useEffect(() => {
    if (isChangingLayout && pendingLayout !== null) {
      requestAnimationFrame(() => {
        requestAnimationFrame(async () => {
          try {
            if (pendingLayout === 'auto') {
              await secureSetItem(MANUAL_SELECTION_FLAG_KEY, false);
              await secureSetItem('selected-layout', '');
              await secureSetItem('website-template', '');
              await secureSetItem('selected-layout-temp', '');
            } else {
              const layoutToSave = String(pendingLayout);
              await secureSetItem('selected-layout-temp', layoutToSave);
              await secureSetItem(MANUAL_SELECTION_FLAG_KEY, true);
            }

            window.location.reload();
          } catch (error) {
            console.error('Error saving temporary layout:', error);
          }
        });
      });
    }
  }, [isChangingLayout, pendingLayout]);

  const closeExpandedPreview = useCallback(() => {
    setExpandedPreview(null);
  }, []);

  useEffect(() => {
    if (!expandedPreview) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeExpandedPreview();
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [expandedPreview, closeExpandedPreview]);

  const handleDialogOpenChange = (open: boolean) => {
    if (!open && expandedPreview) return;
    onOpenChange(open);
  };

  const handleSelectFromPreview = () => {
    if (!expandedPreview) return;
    handleSelectLayout(expandedPreview.id);
  };

  const handleSelectLayout = (layout: SelectableLayout) => {
    if (isChangingLayout) return;

    if (layout === 'auto' && !isManualSelection) return;
    if (layout !== 'auto' && layout === currentLayout && isManualSelection) return;

    setSelectedLayout(layout);
    setExpandedPreview(null);
    onOpenChange(false);

    flushSync(() => {
      setIsChangingLayout(true);
      setPendingLayout(layout);
    });
  };

  const layouts: LayoutOption[] = [
    {
      id: 'auto',
      name: 'Automático',
      description: 'Usa o layout configurado no painel da loja, sem fixar manualmente',
      tag: 'Recomendado',
      image: LAYOUT_PREVIEW_IMAGES[defaultLayout],
      isAuto: true,
    },
    {
      id: 'layout-1',
      name: 'Layout 1',
      description: 'Banner em carrossel, busca sobreposta ao hero, logos das montadoras e cards de ofertas com preço, ano e KM',
      tag: 'Tradicional',
      image: layout1Preview,
      isAuto: false,
    },
    {
      id: 'layout-2',
      name: 'Layout 2',
      description: 'Layout dividido: campo de busca à esquerda e banner promocional à direita, com cards detalhados de specs e opcionais',
      tag: 'Busca lateral',
      image: layout2Preview,
      isAuto: false,
    },
    {
      id: 'layout-3',
      name: 'Layout 3',
      description: 'Banner full-width com busca centralizada, navegação por marcas em pills com contagem e vitrine de ofertas enxuta',
      tag: 'Por marcas',
      image: layout3Preview,
      isAuto: false,
    },
    {
      id: 'layout-4',
      name: 'Layout 4',
      description: 'Topbar com contatos e redes sociais, busca por abas com filtros de marca/modelo/preço e carrossel de marcas',
      tag: 'Motorx',
      image: layout4Preview,
      isAuto: false,
    },
    {
      id: 'layout-5',
      name: 'Layout 5',
      description: 'Layout premium com visual exclusivo — design moderno e sofisticado baseado no Layout 3',
      tag: 'Premium',
      image: layout5Preview,
      isAuto: false,
    },
    {
      id: 'layout-6',
      name: 'Layout 6',
      description: 'Banner full-width com busca centralizada e navegação por marcas — evolução visual do Layout 3 com tipografia Poppins',
      tag: 'Marcas+',
      image: layout6Preview,
      isAuto: false,
    },
    {
      id: 'layout-7',
      name: 'Layout 7',
      description: 'Baseado no Layout 6 — banner full-width, busca centralizada e tipografia Poppins. Pronto para redesign visual',
      tag: 'Novo',
      image: layout7Preview,
      isAuto: false,
    },
  ];

  const isLayoutSelected = (layout: LayoutOption) =>
    layout.id === 'auto'
      ? !isManualSelection
      : selectedLayout === layout.id && isManualSelection;

  const openPreview = (layout: LayoutOption) => {
    if (!layout.image) return;
    setExpandedPreview(layout);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent
          className={cn(
            'sm:max-w-[980px] max-h-[92vh] flex flex-col p-0 gap-0 overflow-hidden',
            expandedPreview && [
              '!fixed !inset-0 !left-0 !top-0 !h-[100dvh] !w-screen !max-w-none !max-h-none',
              '!translate-x-0 !translate-y-0 !rounded-none !border-0 !bg-transparent !shadow-none',
              '[&>button.absolute]:hidden',
            ]
          )}
          onPointerDownOutside={(event) => {
            if (expandedPreview) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (expandedPreview) event.preventDefault();
          }}
          onEscapeKeyDown={(event) => {
            if (expandedPreview) {
              event.preventDefault();
              closeExpandedPreview();
            }
          }}
        >
          <DialogHeader className={cn('px-6 pt-6 pb-4 border-b shrink-0', expandedPreview && 'hidden')}>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Layout className="h-5 w-5" />
              Selecionar Layout
            </DialogTitle>
            <DialogDescription>
              Clique na imagem para ampliar em tela cheia. A seleção será salva na sua sessão.
            </DialogDescription>
          </DialogHeader>

          <div className={cn('px-6 py-5 overflow-y-auto flex-1', expandedPreview && 'hidden')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {layouts.map((layout) => {
                const isSelected = isLayoutSelected(layout);

                return (
                  <article
                    key={layout.id}
                    className={cn(
                      'flex flex-col rounded-xl border bg-card overflow-hidden transition-all duration-200',
                      isSelected
                        ? 'border-primary shadow-md ring-2 ring-primary/15'
                        : 'border-border hover:border-primary/40 hover:shadow-sm'
                    )}
                  >
                    <div className="flex items-start gap-3 p-4 pb-3">
                      <div
                        className={cn(
                          'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
                          isSelected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                        )}
                      >
                        {layout.isAuto ? <Sparkles className="h-5 w-5" /> : <Layout className="h-5 w-5" />}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className={cn('font-semibold text-base', isSelected && 'text-primary')}>
                            {layout.name}
                          </h3>
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                            {layout.tag}
                          </span>
                          {isSelected && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary text-primary-foreground">
                              <Check className="h-3 w-3" />
                              Ativo
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 leading-snug">
                          {layout.description}
                        </p>
                        {layout.isAuto && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Config atual: <strong>{LAYOUT_LABELS[defaultLayout]}</strong>
                          </p>
                        )}
                      </div>
                    </div>

                    {layout.image ? (
                      <button
                        type="button"
                        disabled={isChangingLayout}
                        onClick={() => openPreview(layout)}
                        className="group relative mx-4 mb-3 overflow-hidden rounded-lg border bg-muted/30 aspect-[16/10] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                        aria-label={`Ampliar preview do ${layout.name}`}
                      >
                        <img
                          src={layout.image}
                          alt={`Pré-visualização do ${layout.name}`}
                          className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.02]"
                        />
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/0 group-hover:bg-black/45 transition-colors">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-xs font-semibold text-foreground opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all shadow-sm">
                            <ZoomIn className="h-3.5 w-3.5" />
                            Ampliar preview
                          </span>
                        </div>
                      </button>
                    ) : (
                      <div className="mx-4 mb-3 rounded-lg border bg-gradient-to-br from-primary/10 to-primary/5 aspect-[16/10] flex items-center justify-center">
                        <Sparkles className="h-10 w-10 text-primary/40" />
                      </div>
                    )}

                    <div className="mt-auto flex items-center gap-2 p-4 pt-0">
                      {layout.image && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={isChangingLayout}
                          onClick={() => openPreview(layout)}
                          className="shrink-0"
                        >
                          <ZoomIn className="h-4 w-4 mr-1.5" />
                          Ampliar
                        </Button>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        disabled={isChangingLayout || isSelected}
                        onClick={() => handleSelectLayout(layout.id)}
                        className="flex-1"
                        variant={isSelected ? 'secondary' : 'default'}
                      >
                        {isSelected ? 'Selecionado' : 'Usar este layout'}
                      </Button>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <div className={cn('flex justify-end gap-2 px-6 py-4 border-t shrink-0 bg-muted/20', expandedPreview && 'hidden')}>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isChangingLayout}
            >
              Cancelar
            </Button>
          </div>

          {expandedPreview?.image && (
            <div
              className="absolute inset-0 z-[200] flex flex-col bg-black/95 overflow-hidden"
              role="dialog"
              aria-modal="true"
              aria-label={`Preview ampliado do ${expandedPreview.name}`}
            >
              <div
                className="relative z-[201] flex items-center justify-between gap-4 px-4 sm:px-6 py-3 border-b border-white/10 shrink-0"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="min-w-0">
                  <p className="text-white font-semibold truncate">{expandedPreview.name}</p>
                  <p className="text-white/60 text-sm truncate">{expandedPreview.description}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    type="button"
                    size="sm"
                    disabled={isChangingLayout}
                    onClick={handleSelectFromPreview}
                  >
                    Usar este layout
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10 hover:text-white"
                    onClick={closeExpandedPreview}
                    aria-label="Fechar preview"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              <div
                className="relative flex-1 min-h-0 flex items-center justify-center p-4 sm:p-6 overflow-hidden cursor-zoom-out"
                onClick={closeExpandedPreview}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    closeExpandedPreview();
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label="Fechar preview ampliado"
              >
                <img
                  src={expandedPreview.image}
                  alt={`Preview ampliado do ${expandedPreview.name}`}
                  className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-2xl ring-1 ring-white/10 pointer-events-none select-none"
                  draggable={false}
                />
              </div>

              <div className="relative z-[201] px-4 sm:px-6 py-2 border-t border-white/10 text-center shrink-0 pointer-events-none">
                <p className="text-xs text-white/50">
                  Clique fora da imagem ou pressione Esc para voltar ao seletor
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
  );
};
